import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import ErrorStatusPage from "./pages/ErrorStatusPage";
import "./styles/layout.css";
import { getCurrentUser, logout as logoutApi } from "./services/authService";
import ProtectedRoute from "./components/routing/ProtectedRoute";
import { DEFAULT_PATH } from "./routes/routeConfig";

const DEV_BYPASS_AUTH = false;

function App() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_BYPASS_AUTH);
  const [user, setUser] = useState(DEV_BYPASS_AUTH ? { name: "Admin" } : null);
  const [loading, setLoading] = useState(!DEV_BYPASS_AUTH); // Show loading while checking session

  // Check session on app load using /auth/me endpoint
  useEffect(() => {
    if (DEV_BYPASS_AUTH) return;

    (async () => {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          // Session is valid - store user info for UI display
          sessionStorage.setItem("auth", JSON.stringify({ user: currentUser }));
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          // No valid session
          sessionStorage.removeItem("auth");
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.warn("Session check failed:", err);
        sessionStorage.removeItem("auth");
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const handleHttpError = (event) => {
      const status = Number(event?.detail?.status);
      if (!Number.isInteger(status)) return;

      if (status === 401) {
        sessionStorage.removeItem("auth");
        setUser(null);
        setIsAuthenticated(false);
      }

      navigate(`/error/${status}`, {
        replace: true,
        state: {
          message: event?.detail?.message || "",
        },
      });
    };

    window.addEventListener("app:http-error", handleHttpError);
    return () => window.removeEventListener("app:http-error", handleHttpError);
  }, [navigate]);

  // onLogin expects { user } (no accessToken - it's in HttpOnly cookie now)
  const handleLogin = (authPayload, redirectPath = DEFAULT_PATH) => {
    const { user: nextUser } = authPayload || {};
    if (!nextUser) return;
    // Store user info for UI display only (token is in HttpOnly cookie)
    sessionStorage.setItem("auth", JSON.stringify({ user: nextUser }));
    setIsAuthenticated(true);
    setUser(nextUser);
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  };

  const handleLogout = async () => {
    // Call logout endpoint to clear HttpOnly cookie
    await logoutApi();
    // Clear local session state
    sessionStorage.removeItem("auth");
    try {
      localStorage.removeItem("auth");
    } catch {}
    setIsAuthenticated(false);
    setUser(null);
    navigate("/login", { replace: true });
  };

  // Note: we intentionally do NOT clear on refresh; sessionStorage
  // is automatically cleared when the tab/window is closed.

  if (loading) {
    return (
      <div
        className="App"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  const canAccess = DEV_BYPASS_AUTH || isAuthenticated;

  return (
    <div className="App">
      <Routes>
        <Route
          path="/error/:status"
          element={<ErrorStatusPage isAuthenticated={canAccess} />}
        />
        <Route
          path="/login"
          element={
            canAccess ?
              <Navigate to={DEFAULT_PATH} replace />
            : <Login onLogin={handleLogin} />
          }
        />
        <Route element={<ProtectedRoute isAuthenticated={canAccess} />}>
          <Route
            path="/*"
            element={<Dashboard user={user} onLogout={handleLogout} />}
          />
        </Route>
        <Route
          path="*"
          element={
            <Navigate to={canAccess ? DEFAULT_PATH : "/login"} replace />
          }
        />
      </Routes>
    </div>
  );
}

export default App;
